import { useState } from "react";
import AdminCard from "./AdminCard";

interface Props { onSave: () => void; }

export default function AdminSocialHousing({ onSave }: Props) {
  const [hero, setHero] = useState({
    badge: "Section 21 FSMA 2000 — Regulated Financial Promotion",
    headline: "Social Housing",
    headlineAccent: "Structured Product",
    subtext: "A 5-year, income-generating investment targeting 10.00% per annum — backed by UK assisted living property assets and long-term housing association leases.",
    ctaLabel: "Schedule a Call with Our Team",
    tag1: "10.00% p.a.",
    tag2: "Monthly Income",
    tag3: "5-Year Term",
    tag4: "UK Asset-Backed",
  });

  const [summary, setSummary] = useState({
    title: "Executive Summary",
    para1: "The Thorpe Dellow Social Housing Structured Product is a 5-year, income-generating investment designed to provide monthly distributions backed by UK social housing assets. The investment targets a return of 10.00% per annum, derived from long-term lease agreements with a UK-based housing association operating assisted living properties.",
    para2: "At maturity, investors may elect to receive full capital repayment or roll over into a subsequent term. Investor capital is secured by first legal charges over all underlying properties, held by an Independent Security Trustee.",
    stat1Value: "10.00%",
    stat1Label: "Target Return",
    stat1Sub: "Per annum",
    stat2Value: "Monthly",
    stat2Label: "Income",
    stat2Sub: "Pro rata distributions",
    stat3Value: "5 Years",
    stat3Label: "Term",
    stat3Sub: "With rollover option",
    stat4Value: "UK Social Housing",
    stat4Label: "Asset Class",
    stat4Sub: "Assisted living",
  });

  const [keyTerms, setKeyTerms] = useState([
    { id: 1, label: "Target Return", value: "10.00% per annum" },
    { id: 2, label: "Payment Frequency", value: "Monthly" },
    { id: 3, label: "Investment Term", value: "5 Years" },
    { id: 4, label: "Minimum Investment", value: "£10,000" },
    { id: 5, label: "Currency", value: "GBP (Sterling)" },
    { id: 6, label: "Security", value: "First Legal Charge" },
    { id: 7, label: "Listing", value: "NASDAQ Dubai" },
    { id: 8, label: "Lease Structure", value: "FRI (Full Repairing & Insuring)" },
    { id: 9, label: "Lease Term", value: "15–25 Years" },
    { id: 10, label: "Rent Escalation", value: "CPI + 1% per annum" },
  ]);

  const [faqItems, setFaqItems] = useState([
    { id: 1, q: "Who can invest in this product?", a: "This product is directed solely at Certified High Net Worth Individuals, Self-Certified Sophisticated Investors, or eligible retail investors as defined under the Financial Services and Markets Act 2000 (FSMA)." },
    { id: 2, q: "What is the minimum investment?", a: "The minimum investment is £10,000. There is no maximum investment limit, subject to availability." },
    { id: 3, q: "How and when are distributions paid?", a: "Distributions are paid monthly, directly to your nominated bank account. The first distribution is typically made one month after your subscription is completed." },
    { id: 4, q: "What happens at the end of the 5-year term?", a: "At maturity, you may elect to receive full capital repayment or roll over into a subsequent investment term. You will be contacted by our team at least 90 days before maturity to discuss your options." },
    { id: 5, q: "Is my capital protected?", a: "Capital is at risk. While investor capital is secured by first legal charges over UK social housing properties, this does not guarantee capital repayment. Investors may lose some or all of their capital." },
    { id: 6, q: "Is this product covered by the FSCS?", a: "No. This product is not covered by the Financial Services Compensation Scheme (FSCS). Investors should carefully consider the risk warnings in the full Information Memorandum before investing." },
  ]);

  const [formSection, setFormSection] = useState({
    title: "Expression of Interest",
    subtitle: "Complete the form below and our investment team will contact you within 2 business days. Please ensure you have read and understood the full Information Memorandum before proceeding.",
    successTitle: "Expression of Interest Received",
    successText: "Thank you. Our investment team will review your submission and contact you within 2 business days.",
  });

  const updateKeyTerm = (id: number, k: string, v: string) =>
    setKeyTerms((prev) => prev.map((t) => t.id === id ? { ...t, [k]: v } : t));

  const updateFaq = (id: number, k: string, v: string) =>
    setFaqItems((prev) => prev.map((f) => f.id === id ? { ...f, [k]: v } : f));

  const addFaq = () => {
    const newId = Math.max(...faqItems.map((f) => f.id)) + 1;
    setFaqItems((prev) => [...prev, { id: newId, q: "New Question?", a: "Answer here." }]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>Social Housing Page</h1>
        <p className="text-slate-500 text-sm mt-1">Manage all content on the Social Housing product page — hero, summary, key terms, FAQs, and the expression of interest form.</p>
      </div>

      <AdminCard title="Hero Section">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Regulatory Badge</label>
            <input value={hero.badge} onChange={(e) => setHero({ ...hero, badge: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Headline (Line 1)</label>
              <input value={hero.headline} onChange={(e) => setHero({ ...hero, headline: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Headline Accent (Gold)</label>
              <input value={hero.headlineAccent} onChange={(e) => setHero({ ...hero, headlineAccent: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Subtext</label>
            <textarea value={hero.subtext} onChange={(e) => setHero({ ...hero, subtext: e.target.value })} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[1,2,3,4].map((n) => (
              <div key={n}>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Tag {n}</label>
                <input value={hero[`tag${n}` as keyof typeof hero]} onChange={(e) => setHero({ ...hero, [`tag${n}`]: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
              </div>
            ))}
          </div>
        </div>
      </AdminCard>

      <AdminCard title="Executive Summary">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
            <input value={summary.title} onChange={(e) => setSummary({ ...summary, title: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Paragraph 1</label>
            <textarea value={summary.para1} onChange={(e) => setSummary({ ...summary, para1: e.target.value })} rows={4} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Paragraph 2</label>
            <textarea value={summary.para2} onChange={(e) => setSummary({ ...summary, para2: e.target.value })} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1,2,3,4].map((n) => (
              <div key={n} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                <p className="text-xs font-bold text-slate-500 mb-2">Stat Card {n}</p>
                <div className="space-y-2">
                  <input value={summary[`stat${n}Value` as keyof typeof summary]} onChange={(e) => setSummary({ ...summary, [`stat${n}Value`]: e.target.value })} placeholder="Value" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
                  <input value={summary[`stat${n}Label` as keyof typeof summary]} onChange={(e) => setSummary({ ...summary, [`stat${n}Label`]: e.target.value })} placeholder="Label" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
                  <input value={summary[`stat${n}Sub` as keyof typeof summary]} onChange={(e) => setSummary({ ...summary, [`stat${n}Sub`]: e.target.value })} placeholder="Sub-label" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </AdminCard>

      <AdminCard title="Key Terms Table">
        <div className="space-y-3">
          {keyTerms.map((t) => (
            <div key={t.id} className="grid grid-cols-2 gap-3 bg-slate-50 rounded-xl p-3 border border-slate-100">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Term Label</label>
                <input value={t.label} onChange={(e) => updateKeyTerm(t.id, "label", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Value</label>
                <input value={t.value} onChange={(e) => updateKeyTerm(t.id, "value", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard
        title={`FAQ Section (${faqItems.length} questions)`}
        actions={
          <button onClick={addFaq} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#C9A84C]/10 text-[#C9A84C] text-xs font-semibold hover:bg-[#C9A84C]/20 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-add-line leading-none" />
            Add FAQ
          </button>
        }
      >
        <div className="space-y-4">
          {faqItems.map((f) => (
            <div key={f.id} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-slate-500">FAQ #{f.id}</p>
                <button onClick={() => setFaqItems((prev) => prev.filter((x) => x.id !== f.id))} className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50 transition-colors cursor-pointer">
                  <i className="ri-delete-bin-line text-sm leading-none" />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Question</label>
                  <input value={f.q} onChange={(e) => updateFaq(f.id, "q", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Answer</label>
                  <textarea value={f.a} onChange={(e) => updateFaq(f.id, "a", e.target.value)} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard title="Expression of Interest Form">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Form Title</label>
            <input value={formSection.title} onChange={(e) => setFormSection({ ...formSection, title: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Form Subtitle</label>
            <textarea value={formSection.subtitle} onChange={(e) => setFormSection({ ...formSection, subtitle: e.target.value })} rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Success Title</label>
              <input value={formSection.successTitle} onChange={(e) => setFormSection({ ...formSection, successTitle: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Success Message</label>
              <input value={formSection.successText} onChange={(e) => setFormSection({ ...formSection, successText: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
            </div>
          </div>
        </div>
      </AdminCard>

      <div className="flex justify-end">
        <button onClick={onSave} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#071A2E] text-white text-sm font-semibold hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-save-line leading-none" />
          Save Changes
        </button>
      </div>
    </div>
  );
}
